// One editing surface (Jul 2026, Randy). Extracted from DocEditorModal so the
// Master Editor can host TWO panes side-by-side. Renders the card CONTENT only
// (header + toolbar + editor + footer + save dialog) — the overlay/sizing lives
// in DocEditorModal. `testidSuffix` keeps the second pane's test-ids unique.
import { useEffect, useRef, useState } from "react";
import { X, MagnifyingGlass, ArrowsLeftRight, Wrench, FloppyDisk, ArrowLineUp, ArrowLineDown, TextAlignLeft, Columns } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import TextSearch from "@/components/TextSearch";
import DocSaveDialog from "@/components/DocSaveDialog";

const PER_DOC_KB = { 1: 300, 2: 1000, 3: 5000 };

// Provenance chips — shown ONLY here in the Master Editor (never in the list,
// filename, or file data). Workbench = light blue, Refinery = light orange.
const PROV_META = {
  workbench:     { label: "Workbench", cls: "bg-[#E3EFF7] text-[#2C6E92] border-[#BEDBEC]" },
  file_refinery: { label: "Refinery",  cls: "bg-[#FBEBDB] text-[#B06A2C] border-[#F1D4B6]" },
  impromptu_staging: { label: "Impromptu Staging", cls: "bg-[#E7F4EA] text-[#2F7A43] border-[#BFE0C8]" },
  upload:        { label: "Upload",    cls: "bg-[#F0F1EE] text-[#6B6A68] border-[#E0DED8]" },
  qa:            { label: "Public Q&A", cls: "bg-[#F0F1EE] text-[#6B6A68] border-[#E0DED8]" },
  manual:        { label: "Manual / Paste", cls: "bg-[#F0F1EE] text-[#6B6A68] border-[#E0DED8]" },
};
function provenanceOf(doc) {
  const surface = doc?.source_surface || ({
    refinery: "file_refinery", upload: "upload", conversation: "qa", paste: "manual",
  }[doc?.source_type] || "manual");
  return { surface, meta: PROV_META[surface] || PROV_META.manual, op: doc?.source_operation || null };
}

export default function DocEditorPane({
  doc, siloId, silos = [], onSaved, onClose, onSwitchTab,
  testidSuffix = "",
  onDirtyChange, // reports dirty state up so the shell can guard backdrop/Escape close
  textRef, // shell reads .current to compare the two panes' live text (Compare/diff)
  sideBySide, // { state: "enabled"|"ghost"|"hidden", onClick } — the "Open Side by Side" button
}) {
  const tid = (base) => `${base}${testidSuffix}`;
  const [text, setText] = useState("");
  const [original, setOriginal] = useState("");
  const [loading, setLoading] = useState(true);
  const [showReplace, setShowReplace] = useState(false);
  const [findQ, setFindQ] = useState("");
  const [replaceQ, setReplaceQ] = useState("");
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [wrap, setWrap] = useState(true);
  const [saveOpen, setSaveOpen] = useState(false);
  const taRef = useRef(null);

  useEffect(() => {
    if (!doc) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await api.get(`/silos/${siloId}/docs/${doc.id}/raw`);
        if (cancelled) return;
        setText(r.data.content || "");
        setOriginal(r.data.content || "");
      } catch {
        if (!cancelled) toast.error("Couldn't open this file.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [doc, siloId]);

  useEffect(() => { onDirtyChange?.(text !== original); }, [text, original, onDirtyChange]);
  if (textRef) textRef.current = text;

  if (!doc) return null;

  const dirty = text !== original;
  const words = text.trim() ? text.trim().split(/\s+/).length : 0;
  const bytes = new Blob([text]).size;
  const capKB = PER_DOC_KB[doc.tier] || 5000;
  const pct = Math.min(100, Math.round((bytes / (capKB * 1000)) * 100));
  const meterColor = pct >= 90 ? "#C16E5A" : pct >= 70 ? "#C19A4A" : "#4A5D4E";

  const attemptClose = () => {
    if (dirty && !window.confirm("You have unsaved edits. Close without saving?")) return;
    onClose?.();
  };

  const replaceOne = () => {
    if (!findQ) return;
    const idx = caseSensitive ? text.indexOf(findQ) : text.toLowerCase().indexOf(findQ.toLowerCase());
    if (idx === -1) return toast.message("No match found.");
    setText(text.slice(0, idx) + replaceQ + text.slice(idx + findQ.length));
    toast.success("Replaced 1.");
  };
  const replaceAll = () => {
    if (!findQ) return;
    const escaped = findQ.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re = new RegExp(escaped, caseSensitive ? "g" : "gi");
    const count = (text.match(re) || []).length;
    if (!count) return toast.message("No matches found.");
    setText(text.replace(re, replaceQ));
    toast.success(`Replaced ${count} of ${count}.`);
  };

  const jump = (dir) => {
    const ta = taRef.current; if (!ta) return;
    ta.focus();
    const at = dir < 0 ? 0 : text.length;
    ta.setSelectionRange(at, at);
    ta.scrollTop = dir < 0 ? 0 : ta.scrollHeight;
  };

  const sendToStaging = () => {
    const header = `--- ${doc.title || "Document"} ---\n`;
    sessionStorage.setItem("workbench_seed", JSON.stringify({ staging: true, append: true, title: doc.title, content: header + text }));
    toast.success(`"${doc.title}" sent to the staging area.`);
    onClose?.();
    onSwitchTab?.("workbench");
  };

  const sbs = sideBySide || { state: "hidden" };

  return (
    <div className="flex flex-col h-full min-w-0 flex-1 bg-white" data-testid={tid("doc-editor-pane")}>
      {/* Header */}
      <div className="flex items-center gap-3 px-4 sm:px-5 py-3 border-b hairline shrink-0">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-[#1C1B1A] truncate flex items-center gap-2" data-testid={tid("doc-editor-title")}>
            {doc.title}
            {dirty && <span className="text-[10px] uppercase tracking-[0.18em] text-[#C16E5A]" data-testid={tid("doc-editor-dirty")}>Unsaved</span>}
          </div>
          <div className="text-[11px] text-[#6B6A68] flex items-center gap-1.5 flex-wrap mt-0.5">
            <span>Tier {doc.tier} · editing</span>
            {(() => {
              const p = provenanceOf(doc);
              return (
                <>
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full border text-[9px] uppercase tracking-[0.12em] font-medium ${p.meta.cls}`}
                    data-testid={tid("doc-editor-source-tag")} title={`Created via ${p.meta.label}`}>
                    {p.meta.label}
                  </span>
                  {p.op && (
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full border text-[9px] tracking-[0.02em] max-w-[220px] truncate ${p.meta.cls}`}
                      data-testid={tid("doc-editor-operation-tag")} title={`Operation: ${p.op}`}>
                      {p.op}
                    </span>
                  )}
                </>
              );
            })()}
          </div>
        </div>
        <button onClick={attemptClose} data-testid={tid("doc-editor-close")} className="p-2 rounded-md text-[#6B6A68] hover:bg-[#F0F1EE]"><X size={18} /></button>
      </div>

      {/* Toolbar */}
      <div className="px-4 sm:px-5 py-2.5 border-b hairline bg-[#FDFCF8] shrink-0 space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex-1 min-w-[160px] max-w-md"><TextSearch value={text} textareaRef={taRef} testid={tid("doc-editor-search")} /></div>
          <button onClick={() => setShowReplace((s) => !s)} data-testid={tid("doc-editor-replace-toggle")}
            className={`text-xs px-2.5 py-1.5 rounded-md border hairline flex items-center gap-1.5 ${showReplace ? "bg-[#4A5D4E] text-white border-[#4A5D4E]" : "bg-white text-[#6B6A68] hover:border-[#4A5D4E] hover:text-[#4A5D4E]"}`}>
            <ArrowsLeftRight size={13} /> Find &amp; Replace
          </button>
          <button onClick={() => jump(-1)} title="Jump to top" data-testid={tid("doc-editor-top")} className="p-1.5 rounded-md text-[#6B6A68] hover:text-[#4A5D4E] hover:bg-[#F0F1EE]"><ArrowLineUp size={14} /></button>
          <button onClick={() => jump(1)} title="Jump to bottom" data-testid={tid("doc-editor-bottom")} className="p-1.5 rounded-md text-[#6B6A68] hover:text-[#4A5D4E] hover:bg-[#F0F1EE]"><ArrowLineDown size={14} /></button>
          <button onClick={() => setWrap((w) => !w)} title="Toggle line wrap" data-testid={tid("doc-editor-wrap")}
            className={`p-1.5 rounded-md hover:bg-[#F0F1EE] ${wrap ? "text-[#4A5D4E]" : "text-[#9A9890]"}`}><TextAlignLeft size={14} /></button>
          {sbs.state !== "hidden" && (
            <button
              onClick={sbs.onClick}
              data-testid={tid("doc-editor-sidebyside")}
              title={sbs.state === "ghost" ? "Turn your phone to landscape to open side-by-side" : "Open a second file side-by-side"}
              className={`ml-auto text-xs px-2.5 py-1.5 rounded-md border hairline flex items-center gap-1.5 shrink-0 ${sbs.state === "ghost" ? "bg-white text-[#B7B5AE] border-[#EAE8E2] cursor-pointer" : "bg-white text-[#4A5D4E] hover:border-[#4A5D4E] hover:bg-[#EAF3EC]"}`}
            >
              <Columns size={14} weight="duotone" /> Open Side by Side
            </button>
          )}
        </div>
        {showReplace && (
          <div className="flex items-center gap-2 flex-wrap" data-testid={tid("doc-editor-replace-bar")}>
            <div className="relative flex-1 min-w-[140px] max-w-xs">
              <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#6B6A68]" />
              <input value={findQ} onChange={(e) => setFindQ(e.target.value)} placeholder="Find…" data-testid={tid("doc-editor-find-input")}
                className="w-full bg-white border hairline rounded-md pl-7 pr-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]" />
            </div>
            <input value={replaceQ} onChange={(e) => setReplaceQ(e.target.value)} placeholder="Replace with…" data-testid={tid("doc-editor-replace-input")}
              className="flex-1 min-w-[140px] max-w-xs bg-white border hairline rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]" />
            <button onClick={replaceOne} disabled={!findQ} data-testid={tid("doc-editor-replace-one")}
              className="text-xs px-2.5 py-1 rounded-md border hairline bg-white text-[#4A5D4E] hover:border-[#4A5D4E] disabled:opacity-40">Replace</button>
            <button onClick={replaceAll} disabled={!findQ} data-testid={tid("doc-editor-replace-all")}
              className="text-xs px-2.5 py-1 rounded-md bg-[#4A5D4E] text-white hover:bg-[#3A4A3E] disabled:opacity-40">Replace all</button>
            <label className="flex items-center gap-1 text-[11px] text-[#6B6A68] cursor-pointer" data-testid={tid("doc-editor-case")}>
              <input type="checkbox" checked={caseSensitive} onChange={(e) => setCaseSensitive(e.target.checked)} /> Aa
            </label>
          </div>
        )}
      </div>

      {/* Editor surface */}
      <div className="flex-1 min-h-0 p-4 sm:p-5">
        {loading ? (
          <div className="h-full flex items-center justify-center text-sm text-[#6B6A68]">Opening file…</div>
        ) : (
          <textarea
            ref={taRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            data-testid={tid("doc-editor-textarea")}
            spellCheck={false}
            wrap={wrap ? "soft" : "off"}
            className="w-full h-full resize-none bg-[#FDFCF8] border hairline rounded-lg p-4 font-mono text-sm leading-relaxed text-[#1C1B1A] focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
            placeholder="This file is empty — start typing."
          />
        )}
      </div>

      {/* Footer */}
      <div className="px-4 sm:px-5 py-3 border-t hairline bg-[#FDFCF8] shrink-0 flex items-center gap-3 flex-wrap">
        <div className="text-[11px] text-[#6B6A68] flex items-center gap-3 flex-1 min-w-[140px]">
          <span data-testid={tid("doc-editor-counts")}>{text.length.toLocaleString()} chars · {words.toLocaleString()} words · ~{Math.max(1, Math.round(words / 200))} min read</span>
          <span className="hidden lg:flex items-center gap-1.5">
            <span className="w-20 h-1.5 rounded-full bg-[#F0F1EE] overflow-hidden inline-block">
              <span className="h-full block" style={{ width: `${pct}%`, background: meterColor }} />
            </span>
            <span style={{ color: meterColor }}>{pct}% of Tier {doc.tier} cap</span>
          </span>
        </div>
        <button onClick={() => setText(original)} disabled={!dirty} data-testid={tid("doc-editor-revert")}
          className="text-xs px-3 py-2 rounded-md border hairline bg-white text-[#6B6A68] hover:border-[#C16E5A] hover:text-[#C16E5A] disabled:opacity-40">Revert</button>
        <button onClick={sendToStaging} data-testid={tid("doc-editor-send-staging")}
          className="text-xs px-3 py-2 rounded-md border hairline bg-white text-[#4A5D4E] hover:border-[#4A5D4E] flex items-center gap-1.5"><Wrench size={14} weight="duotone" /> Send to staging</button>
        <button onClick={() => setSaveOpen(true)} data-testid={tid("doc-editor-save")}
          className="text-xs px-4 py-2 rounded-md bg-[#1C1B1A] text-white hover:bg-[#3A4A3E] flex items-center gap-1.5"><FloppyDisk size={14} /> Save…</button>
      </div>

      <DocSaveDialog
        open={saveOpen}
        onClose={() => setSaveOpen(false)}
        content={text}
        sourceSiloId={siloId}
        sourceDoc={doc}
        silos={silos}
        onSaved={() => { setOriginal(text); onSaved?.(); }}
      />
    </div>
  );
}
